import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  Megaphone,
  Send,
  Rocket,
  Plus,
  StickyNote,
  GraduationCap,
  Ban
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../lib/api';
import {
  klassenbuchApi,
  type KlassenbuchDetail,
  type KlassenbuchEntry,
  type KlassenbuchParticipant,
  type SchedulePeriod,
  type UnitType,
  type AttendanceStatus,
  type ScheduleConfigDocument,
  type SchoolDay,
  type KlassenbuchTeacher,
  type CurriculumLink,
  type CompetencySuggestion,
  type StaffNote,
  type SchoolEvent,
  type WeekScheduleSlot,
  getScheduleGridById
} from '../lib/klassenbuchApi';
import LessonContentEditor from '../components/klassenbuch/LessonContentEditor';
import AttendanceSeatplan from '../components/klassenbuch/AttendanceSeatplan';
import ExcuseModal from '../components/klassenbuch/ExcuseModal';
import AuditPanel from '../components/klassenbuch/AuditPanel';
import AcknowledgmentBadge from '../components/klassenbuch/AcknowledgmentBadge';
import QuickLessonMode from '../components/klassenbuch/QuickLessonMode';

type UnitOption = {
  unitType: UnitType;
  unitId: number;
  label: string;
  scheduleGridId?: string | null;
};

type SchoolYear = {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
};

function mondayOf(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekRangesToProvision(viewWeekStart: Date): Array<{ week_start: string; week_end: string }> {
  const unique = new Map<string, { week_start: string; week_end: string }>();
  for (const monday of [mondayOf(new Date()), addDays(mondayOf(new Date()), 7), mondayOf(viewWeekStart)]) {
    const week_start = toDateStr(monday);
    if (!unique.has(week_start)) {
      unique.set(week_start, {
        week_start,
        week_end: toDateStr(addDays(monday, 6))
      });
    }
  }
  return [...unique.values()];
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

const TABS = ['content', 'homework', 'attendance', 'excuses', 'curriculum', 'performance', 'notes', 'calendar', 'audit'] as const;
type Tab = (typeof TABS)[number];

const DAY_INDEX: Record<SchoolDay, number> = {
  mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6
};
const DAY_SHORT: Record<SchoolDay, string> = {
  mon: 'Mo', tue: 'Di', wed: 'Mi', thu: 'Do', fri: 'Fr', sat: 'Sa', sun: 'So'
};

const KLASSENBUCH_PREFS_KEY = 'klassenbuch.preferences';

type KlassenbuchPreferences = {
  unitType?: UnitType;
  unitId?: number | null;
  schoolYearId?: number | null;
  weekStart?: string;
};

function readKlassenbuchPreferences(): KlassenbuchPreferences {
  try {
    const raw = localStorage.getItem(KLASSENBUCH_PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as KlassenbuchPreferences;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeKlassenbuchPreferences(prefs: KlassenbuchPreferences) {
  try {
    localStorage.setItem(KLASSENBUCH_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore quota / private mode
  }
}

function hhmmToMinutes(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = String(hhmm).match(/^(\d{2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function startMinutesOfEntry(e: KlassenbuchEntry): number | null {
  if (!e.start_at) return null;
  const d = new Date(e.start_at);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

function normalizeDisplayPeriodLabel(
  period: { period_no: number; label: string | null; is_break: boolean },
  _index: number
): string {
  if (period.is_break) return period.label || 'Pause';
  const trimmed = String(period.label || '').trim();
  if (trimmed) return trimmed;
  const numericNo = Number(period.period_no);
  const fallback = Number.isFinite(numericNo) ? numericNo : 1;
  return `${fallback}. Stunde`;
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

type EntryColorSource = {
  source?: string | null;
  topic_summary?: string | null;
  lesson_content_html?: string | null;
  homework_html?: string | null;
  has_homework?: number | boolean;
  attendance_count?: number;
};

function computeHasManualContent(source: EntryColorSource): boolean {
  if (source.source === 'manual') return true;
  if ((source.attendance_count || 0) > 0) return true;
  if (isTruthyFlag(source.has_homework)) return true;
  if (String(source.topic_summary || '').trim()) return true;
  if (stripHtml(source.lesson_content_html)) return true;
  if (stripHtml(source.homework_html)) return true;
  return false;
}

function applyEntryGridPatch(
  entry: KlassenbuchEntry,
  patch: EntryColorSource & {
    is_cancelled?: number | boolean;
    homework_due_date?: string | null;
    is_substitution?: number;
  },
  detailUnit?: { lesson_content_html?: string | null; homework_html?: string | null } | null
): KlassenbuchEntry {
  const topicSummary = 'topic_summary' in patch ? patch.topic_summary : entry.topic_summary;
  const lessonHtml =
    patch.lesson_content_html !== undefined
      ? patch.lesson_content_html
      : detailUnit?.lesson_content_html ?? null;
  const homeworkHtml =
    patch.homework_html !== undefined ? patch.homework_html : detailUnit?.homework_html ?? null;
  const hasHomework =
    'homework_html' in patch ? Boolean(stripHtml(patch.homework_html)) : isTruthyFlag(entry.has_homework);
  const isCancelled = 'is_cancelled' in patch ? patch.is_cancelled : entry.is_cancelled;

  const merged: KlassenbuchEntry = {
    ...entry,
    ...(topicSummary !== undefined ? { topic_summary: topicSummary ?? null } : {}),
    ...(patch.homework_due_date !== undefined ? { homework_due_date: patch.homework_due_date } : {}),
    ...(patch.is_substitution !== undefined ? { is_substitution: patch.is_substitution } : {}),
    ...(isCancelled !== undefined ? { is_cancelled: isCancelled } : {}),
    ...(patch.attendance_count !== undefined ? { attendance_count: patch.attendance_count } : {}),
    ...( 'homework_html' in patch ? { has_homework: hasHomework ? 1 : 0 } : {})
  };

  return {
    ...merged,
    has_manual_content: computeHasManualContent({
      source: merged.source,
      topic_summary: merged.topic_summary,
      lesson_content_html: lessonHtml,
      homework_html: homeworkHtml,
      has_homework: merged.has_homework,
      attendance_count: merged.attendance_count
    })
  };
}

function entryHasManualContent(entry: KlassenbuchEntry): boolean {
  return isTruthyFlag(entry.has_manual_content) || computeHasManualContent(entry);
}

function isCellPast(day: Date, period: { end_time: string }): boolean {
  const endMinutes = hhmmToMinutes(period.end_time);
  if (endMinutes == null) return false;
  const now = new Date();
  const cellEnd = new Date(day);
  cellEnd.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
  return now > cellEnd;
}

function entryCellClass(entry: KlassenbuchEntry, selected: boolean): string {
  if (selected) {
    return 'border-blue-500 bg-blue-100 dark:border-blue-400 dark:bg-blue-950/60';
  }
  if (isTruthyFlag(entry.is_cancelled)) {
    return 'border-red-300 bg-red-50/70 dark:border-red-700 dark:bg-red-950/30';
  }
  if (entryHasManualContent(entry)) {
    return 'border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-950/40';
  }
  return 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800';
}

type EntryButtonProps = {
  entry: KlassenbuchEntry;
  selected: boolean;
  onClick: () => void;
  compact?: boolean;
};

function EntryButton({ entry, selected, onClick, compact = false }: EntryButtonProps) {
  const totalMarked = entry.attendance_count || 0;
  const absent = entry.absent_count || 0;
  const late = entry.late_count || 0;
  const cancelled = isTruthyFlag(entry.is_cancelled);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden w-full text-left rounded-md px-2 py-1 border text-xs ${entryCellClass(entry, selected)} ${cancelled ? 'kb-cell-cancelled' : ''}`}
    >
      <div className="flex items-center gap-1">
        <div className="font-medium text-gray-900 dark:text-gray-100 truncate flex-1">{entry.title}</div>
        {entry.klassenbuch_locked_at && <Lock size={12} className="text-emerald-600" />}
      </div>
      {(entry.subject_code || entry.subject_name) && (
        <div className="mt-0.5 text-xs text-indigo-700 dark:text-indigo-300 truncate">
          {entry.subject_code || entry.subject_name}
        </div>
      )}
      {!compact && entry.topic_summary && (
        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{entry.topic_summary}</div>
      )}
      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        {entry.start_at && (
          <span>
            {new Date(entry.start_at).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        )}
        {totalMarked > 0 && (
          <>
            {absent > 0 && <span className="text-rose-600">F{absent}</span>}
            {late > 0 && <span className="text-amber-600">V{late}</span>}
          </>
        )}
        {entry.has_homework ? <span className="text-blue-600">HA</span> : null}
      </div>
    </button>
  );
}

type FragmentRowProps = {
  period: { period_no: number; label: string | null; start_time: string; end_time: string; is_break: boolean };
  displayLabel: string;
  weekDays: Date[];
  map: Record<string, KlassenbuchEntry[]>;
  scheduleSlots: Record<string, WeekScheduleSlot>;
  selectedEntryId: number | null;
  onSelect: (id: number) => void;
  onCreate?: (date: Date, periodNo: number) => void;
  canCreate: boolean;
  creatingKey: string | null;
};

function FragmentRow({
  period,
  displayLabel,
  weekDays,
  map,
  scheduleSlots,
  selectedEntryId,
  onSelect,
  onCreate,
  canCreate,
  creatingKey
}: FragmentRowProps) {
  return (
    <>
      <div
        className={`text-xs leading-tight px-2 py-1.5 rounded-md border ${
          period.is_break
            ? 'bg-gray-50 border-gray-200 text-gray-500 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400'
            : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200'
        }`}
      >
        <div className="font-semibold">
          {displayLabel}
        </div>
        <div className="text-gray-500 dark:text-gray-400">
          {period.start_time}–{period.end_time}
        </div>
      </div>
      {weekDays.map((d) => {
        const key = toDateStr(d);
        const cellKey = `${key}|${period.period_no}`;
        const list = map[cellKey] || [];
        const isToday = key === toDateStr(new Date());
        const busy = creatingKey === cellKey;
        const slot = scheduleSlots[cellKey];
        const isPast = !period.is_break && isCellPast(d, period);
        const emptyCellClass = slot && isPast
          ? 'border-yellow-300 bg-yellow-100/90 dark:border-yellow-700 dark:bg-yellow-950/50'
          : isToday
          ? 'border-blue-200 bg-blue-50/30 dark:border-blue-700 dark:bg-blue-950/40'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900';
        return (
          <div
            key={cellKey}
            className={`rounded-md border min-h-[52px] p-1 ${
              period.is_break
                ? 'border-dashed border-gray-200 bg-gray-50/60 dark:border-gray-700 dark:bg-gray-900/50'
                : emptyCellClass
            }`}
          >
            {period.is_break ? (
              <div className="text-xs text-gray-400 dark:text-gray-500 italic">Pause</div>
            ) : list.length === 0 ? (
              slot && isPast ? (
                <div className="w-full h-full min-h-[44px] rounded px-1 py-1 flex flex-col justify-center">
                  <div className="text-xs font-medium text-yellow-900 dark:text-yellow-100 truncate">{slot.title}</div>
                  <div className="text-[10px] text-yellow-800/80 dark:text-yellow-200/80">Kein Eintrag</div>
                  {canCreate && onCreate ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onCreate(d, period.period_no)}
                      className="mt-1 text-[10px] text-yellow-900/70 dark:text-yellow-100/70 hover:underline disabled:opacity-50 self-start"
                      title="Stunde nachtragen"
                    >
                      {busy ? 'Lege an…' : 'Nachtragen'}
                    </button>
                  ) : null}
                </div>
              ) : canCreate && onCreate ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onCreate(d, period.period_no)}
                  className="w-full h-full min-h-[44px] text-xs text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50/60 dark:hover:bg-blue-900/30 rounded flex flex-col items-center justify-center gap-0.5 disabled:opacity-50"
                  title="Stunde nachtragen"
                >
                  {slot ? (
                    <>
                      <span className="font-medium text-gray-600 dark:text-gray-300 truncate max-w-full px-1">{slot.title}</span>
                      <span className="inline-flex items-center gap-1">
                        <Plus size={12} />
                        {busy ? 'Lege an…' : 'Nachtragen'}
                      </span>
                    </>
                  ) : (
                    <>
                      <Plus size={12} />
                      {busy ? 'Lege an…' : 'Nachtragen'}
                    </>
                  )}
                </button>
              ) : (
                <div className="text-xs text-gray-300 dark:text-gray-600">–</div>
              )
            ) : (
              <div className="space-y-1">
                {list.map((e) => (
                  <EntryButton
                    key={e.id}
                    entry={e}
                    selected={selectedEntryId === e.id}
                    onClick={() => onSelect(e.id)}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export default function Klassenbuch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [unitOptions, setUnitOptions] = useState<UnitOption[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);

  const savedPrefs = readKlassenbuchPreferences();
  const initialUnitType =
    (searchParams.get('unit_type') as UnitType) || savedPrefs.unitType || 'class';
  const initialUnitId = searchParams.get('unit_id')
    ? Number(searchParams.get('unit_id'))
    : savedPrefs.unitId ?? null;
  const initialDate =
    searchParams.get('date') || savedPrefs.weekStart || toDateStr(new Date());
  const initialSchoolYearId = searchParams.get('school_year_id')
    ? Number(searchParams.get('school_year_id'))
    : savedPrefs.schoolYearId ?? null;
  const initialUnitIdRef = searchParams.get('teaching_unit_id');

  const [unitType, setUnitType] = useState<UnitType>(initialUnitType);
  const [unitId, setUnitId] = useState<number | null>(initialUnitId);
  const [schoolYearId, setSchoolYearId] = useState<number | null>(initialSchoolYearId);
  const [weekStart, setWeekStart] = useState<Date>(mondayOf(new Date(initialDate)));
  const [entries, setEntries] = useState<KlassenbuchEntry[]>([]);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(
    initialUnitIdRef ? Number(initialUnitIdRef) : null
  );
  const [detail, setDetail] = useState<KlassenbuchDetail | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('content');
  const [quickMode, setQuickMode] = useState(false);
  const [excuseParticipant, setExcuseParticipant] = useState<KlassenbuchParticipant | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [scheduleConfigDoc, setScheduleConfigDoc] = useState<ScheduleConfigDocument | null>(null);
  const [schedulePeriods, setSchedulePeriods] = useState<SchedulePeriod[] | null>(null);
  const [creatingCellKey, setCreatingCellKey] = useState<string | null>(null);
  const [weekScheduleSlots, setWeekScheduleSlots] = useState<WeekScheduleSlot[]>([]);
  const [, setNowTick] = useState(0);
  const [teachers, setTeachers] = useState<KlassenbuchTeacher[]>([]);
  const [curriculumLinks, setCurriculumLinks] = useState<CurriculumLink[]>([]);
  const [competencySuggestions, setCompetencySuggestions] = useState<CompetencySuggestion[]>([]);
  const [unitGrades, setUnitGrades] = useState<any[]>([]);
  const [staffNotes, setStaffNotes] = useState<StaffNote[]>([]);
  const [events, setEvents] = useState<SchoolEvent[]>([]);

  // --- Schedule config ---
  useEffect(() => {
    klassenbuchApi
      .getConfig()
      .then(setScheduleConfigDoc)
      .catch((err) => {
        console.warn('[Klassenbuch] getConfig fehlgeschlagen, Fallback auf Tages-Layout', err);
        setScheduleConfigDoc(null);
      });
  }, []);

  // --- Initial data ---
  useEffect(() => {
    (async () => {
      try {
        const [classes, courses, groups, schoolYearsRes, teachersRes] = await Promise.all([
          apiClient.get('/units/class').catch(() => ({ data: { data: [] } })),
          apiClient.get('/units/course').catch(() => ({ data: { data: [] } })),
          apiClient.get('/units/group').catch(() => ({ data: { data: [] } })),
          apiClient.get('/school-years').catch(() => ({ data: { data: [] } })),
          klassenbuchApi.listTeachers().catch(() => [])
        ]);
        const opts: UnitOption[] = [];
        for (const c of (classes.data.data || []) as Array<{ id: number; class_name: string; schedule_grid_id?: string | null }>) {
          opts.push({
            unitType: 'class',
            unitId: c.id,
            label: `Klasse · ${c.class_name}`,
            scheduleGridId: c.schedule_grid_id || null
          });
        }
        for (const c of (courses.data.data || []) as Array<{ id: number; name: string; fach?: string }>) {
          opts.push({ unitType: 'course', unitId: c.id, label: `Kurs · ${c.name}${c.fach ? ' (' + c.fach + ')' : ''}` });
        }
        for (const g of (groups.data.data || []) as Array<{ id: number; name: string }>) {
          opts.push({ unitType: 'group', unitId: g.id, label: `Gruppe · ${g.name}` });
        }
        setUnitOptions(opts);
        const ys = (schoolYearsRes.data?.data || []) as SchoolYear[];
        setSchoolYears(ys);
        setTeachers(teachersRes || []);

        const saved = readKlassenbuchPreferences();
        const validUnit = (type: UnitType, id: number | null) =>
          id != null && opts.some((o) => o.unitType === type && o.unitId === id);

        if (!validUnit(unitType, unitId)) {
          if (saved.unitType && saved.unitId && validUnit(saved.unitType, saved.unitId)) {
            setUnitType(saved.unitType);
            setUnitId(saved.unitId);
          } else if (unitId != null) {
            setUnitId(null);
          }
        }

        setSchoolYearId((prev) => {
          if (prev != null && ys.some((y) => y.id === prev)) return prev;
          const today = toDateStr(new Date());
          const current = ys.find((y) => y.start_date <= today && y.end_date >= today);
          return current?.id ?? prev;
        });
      } catch (err) {
        console.error('[Klassenbuch] initial load failed', err);
      }
    })();
  }, []);

  // --- URL sync + persist selection ---
  useEffect(() => {
    writeKlassenbuchPreferences({
      unitType,
      unitId,
      schoolYearId,
      weekStart: toDateStr(weekStart)
    });

    const next = new URLSearchParams(searchParams);
    next.set('unit_type', unitType);
    if (unitId) next.set('unit_id', String(unitId));
    else next.delete('unit_id');
    if (schoolYearId) next.set('school_year_id', String(schoolYearId));
    else next.delete('school_year_id');
    next.set('date', toDateStr(weekStart));
    if (selectedEntryId) next.set('teaching_unit_id', String(selectedEntryId));
    else next.delete('teaching_unit_id');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType, unitId, schoolYearId, weekStart, selectedEntryId]);

  // --- Week entries + auto-provision (current week) + schedule slots ---
  useEffect(() => {
    if (!unitType || !unitId) {
      setEntries([]);
      setWeekScheduleSlots([]);
      return;
    }

    let cancelled = false;
    const from = toDateStr(weekStart);
    const to = toDateStr(addDays(weekStart, 6));
    const weeksToEnsure = getWeekRangesToProvision(weekStart);

    (async () => {
      try {
        for (const week of weeksToEnsure) {
          await klassenbuchApi.ensureWeekEntries({
            parent_type: unitType,
            parent_id: unitId,
            week_start: week.week_start,
            week_end: week.week_end,
            school_year_id: schoolYearId
          });
        }
        const [freshEntries, slots] = await Promise.all([
          klassenbuchApi.listEntries({ unitType, unitId, schoolYearId, from, to }),
          klassenbuchApi
            .getWeekScheduleSlots({
              parent_type: unitType,
              parent_id: unitId,
              week_start: from,
              week_end: to,
              school_year_id: schoolYearId
            })
            .catch(() => [] as WeekScheduleSlot[])
        ]);
        if (!cancelled) {
          setEntries(freshEntries);
          setWeekScheduleSlots(slots);
        }
      } catch (err) {
        console.error('[Klassenbuch] week load failed', err);
        if (!cancelled) {
          setEntries([]);
          setWeekScheduleSlots([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [unitType, unitId, schoolYearId, weekStart]);

  // Gelbe Markierung für vergangene leere Zellen aktualisiert sich mit der Uhrzeit
  useEffect(() => {
    const timer = window.setInterval(() => setNowTick((tick) => tick + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!unitType || !unitId) {
      setSchedulePeriods(null);
      return;
    }
    klassenbuchApi
      .getSchedulePeriods({
        parent_type: unitType,
        parent_id: unitId,
        date: toDateStr(weekStart)
      })
      .then((payload) => {
        setSchedulePeriods(Array.isArray(payload.periods) ? payload.periods : []);
      })
      .catch((err) => {
        console.warn('[Klassenbuch] getSchedulePeriods fehlgeschlagen, fallback auf Config', err);
        setSchedulePeriods(null);
      });
  }, [unitType, unitId, weekStart]);

  const scheduleSlotsByCell = useMemo(() => {
    const map: Record<string, WeekScheduleSlot> = {};
    for (const slot of weekScheduleSlots) {
      map[`${slot.date}|${slot.period_no}`] = slot;
    }
    return map;
  }, [weekScheduleSlots]);

  // --- Detail ---
  useEffect(() => {
    if (!selectedEntryId) {
      setDetail(null);
      setCurriculumLinks([]);
      setCompetencySuggestions([]);
      setUnitGrades([]);
      setStaffNotes([]);
      return;
    }
    setLoadingDetail(true);
    Promise.all([
      klassenbuchApi.getEntry(selectedEntryId),
      klassenbuchApi.getCurriculumLinks(selectedEntryId).catch(() => []),
      klassenbuchApi.suggestCompetencies(selectedEntryId).catch(() => []),
      klassenbuchApi.listUnitGrades(selectedEntryId).catch(() => []),
      klassenbuchApi.listStaffNotes({ scope_type: 'teaching_unit', scope_id: String(selectedEntryId) }).catch(() => [])
    ])
      .then(([entryDetail, links, suggestions, grades, notes]) => {
        setDetail(entryDetail);
        setCurriculumLinks(links);
        setCompetencySuggestions(suggestions);
        setUnitGrades(grades);
        setStaffNotes(notes);
      })
      .catch((err) => {
        console.error('[Klassenbuch] getEntry', err);
        setDetail(null);
      })
      .finally(() => setLoadingDetail(false));
  }, [selectedEntryId]);

  useEffect(() => {
    const from = toDateStr(weekStart);
    const to = toDateStr(addDays(weekStart, 6));
    klassenbuchApi.listEvents(from, to).then(setEvents).catch(() => setEvents([]));
  }, [weekStart]);

  const classScheduleGridId = useMemo(() => {
    if (unitType !== 'class' || !unitId) return null;
    const opt = unitOptions.find((item) => item.unitType === 'class' && item.unitId === unitId);
    return opt?.scheduleGridId || null;
  }, [unitType, unitId, unitOptions]);

  const effectiveScheduleGrid = useMemo(() => {
    if (!scheduleConfigDoc) return null;
    return getScheduleGridById(scheduleConfigDoc, classScheduleGridId);
  }, [scheduleConfigDoc, classScheduleGridId]);

  const activeDays: SchoolDay[] = useMemo(() => {
    const fallback: SchoolDay[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
    if (!effectiveScheduleGrid?.schoolDays?.length) return fallback;
    const order: SchoolDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    return order.filter((d) => effectiveScheduleGrid.schoolDays.includes(d));
  }, [effectiveScheduleGrid]);

  const weekDays = useMemo(() => {
    return activeDays.map((d) => addDays(weekStart, DAY_INDEX[d]));
  }, [weekStart, activeDays]);

  const periods = useMemo(() => {
    if (Array.isArray(schedulePeriods) && schedulePeriods.length > 0) return schedulePeriods;
    return effectiveScheduleGrid?.periods ?? [];
  }, [effectiveScheduleGrid, schedulePeriods]);

  // Map: `${dayKey}|${period_no}` -> KlassenbuchEntry[]; "unassigned" for entries
  // that do not fit any period window (e.g. ad-hoc lessons outside the raster).
  const entriesByCell = useMemo(() => {
    const map: Record<string, KlassenbuchEntry[]> = {};
    const unassignedByDay: Record<string, KlassenbuchEntry[]> = {};
    if (!periods.length) {
      for (const e of entries) {
        const key = e.start_at ? toDateStr(new Date(e.start_at)) : 'unscheduled';
        if (!unassignedByDay[key]) unassignedByDay[key] = [];
        unassignedByDay[key].push(e);
      }
      return { map, unassignedByDay };
    }
    for (const e of entries) {
      const dayKey = e.start_at ? toDateStr(new Date(e.start_at)) : null;
      const startMin = startMinutesOfEntry(e);
      let placed = false;
      if (dayKey && startMin != null) {
        for (const p of periods) {
          const ps = hhmmToMinutes(p.start_time);
          const pe = hhmmToMinutes(p.end_time);
          if (ps == null || pe == null) continue;
          if (startMin >= ps && startMin < pe) {
            const k = `${dayKey}|${p.period_no}`;
            if (!map[k]) map[k] = [];
            map[k].push(e);
            placed = true;
            break;
          }
        }
      }
      if (!placed) {
        const k = dayKey || 'unscheduled';
        if (!unassignedByDay[k]) unassignedByDay[k] = [];
        unassignedByDay[k].push(e);
      }
    }
    return { map, unassignedByDay };
  }, [entries, periods]);

  // legacy helper retained for non-raster (fallback) paths
  const entriesByDay = useMemo(() => {
    const map: Record<string, KlassenbuchEntry[]> = {};
    for (const e of entries) {
      const key = e.start_at ? toDateStr(new Date(e.start_at)) : 'unscheduled';
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [entries]);

  // --- Actions ---

  async function createEntryForCell(d: Date, periodNo: number) {
    if (!unitType || !unitId) {
      toast.error('Bitte zuerst eine Klasse, Kurs oder Gruppe waehlen.');
      return;
    }
    const dateStr = toDateStr(d);
    const cellKey = `${dateStr}|${periodNo}`;
    setCreatingCellKey(cellKey);
    try {
      const created = await klassenbuchApi.createEntry({
        parent_type: unitType,
        parent_id: unitId,
        date: dateStr,
        period_no: periodNo
      });
      const slot = weekScheduleSlots.find((s) => s.date === dateStr && s.period_no === periodNo);
      setEntries((prev) => {
        if (prev.some((entry) => entry.id === created.id)) return prev;
        const optimistic: KlassenbuchEntry = {
          id: created.id,
          uid: String(created.id),
          title: created.title,
          topic_summary: null,
          parent_type: unitType,
          parent_id: unitId,
          subject_code: slot?.subject_code ?? null,
          status: 'draft',
          start_at: created.start_at,
          end_at: created.end_at,
          has_homework: 0,
          homework_due_date: null,
          is_substitution: 0,
          substitution_for_user_id: null,
          klassenbuch_locked_at: null,
          source: slot ? 'timetable' : 'manual',
          is_cancelled: 0,
          has_manual_content: false,
          attendance_count: 0,
          absent_count: 0,
          late_count: 0,
          excused_count: 0,
          beurlaubt_count: 0
        };
        return [...prev, optimistic];
      });
      setSelectedEntryId(created.id);
      toast.success('Stunde angelegt – jetzt Inhalt nachtragen');
      const from = toDateStr(weekStart);
      const to = toDateStr(addDays(weekStart, 6));
      void klassenbuchApi.listEntries({ unitType, unitId, schoolYearId, from, to }).then(setEntries);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Anlegen fehlgeschlagen');
    } finally {
      setCreatingCellKey(null);
    }
  }

  type SaveDetailFieldsInput = Partial<{
    title: string;
    topic_summary: string | null;
    lesson_content_html: string | null;
    homework_html: string | null;
    homework_due_date: string | null;
    is_substitution: 0 | 1;
    substitution_for_user_id: number | null;
    substitution_reason?: string | null;
    is_cancelled: 0 | 1;
  }>;

  async function saveDetailFields(fields: SaveDetailFieldsInput) {
    if (!selectedEntryId) return;
    const entryId = selectedEntryId;

    setDetail((prev) => (prev ? { ...prev, unit: { ...prev.unit, ...fields } } : prev));
    setEntries((prev) =>
      prev.map((e) =>
        e.id === entryId ? applyEntryGridPatch(e, fields, detail ? { ...detail.unit, ...fields } : null) : e
      )
    );

    try {
      const updated = await klassenbuchApi.updateEntry(entryId, fields);
      setDetail((prev) => (prev ? { ...prev, unit: { ...prev.unit, ...updated } } : prev));
      setEntries((prev) =>
        prev.map((e) => (e.id === entryId ? applyEntryGridPatch(e, updated, updated) : e))
      );
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen');
      const from = toDateStr(weekStart);
      const to = toDateStr(addDays(weekStart, 6));
      if (unitType && unitId) {
        const fresh = await klassenbuchApi.listEntries({ unitType, unitId, schoolYearId, from, to });
        setEntries(fresh);
        if (selectedEntryId) {
          const refreshedDetail = await klassenbuchApi.getEntry(selectedEntryId);
          setDetail(refreshedDetail);
        }
      }
    }
  }

  async function handleSetAttendance(studentKey: string, status: AttendanceStatus) {
    if (!selectedEntryId || !detail) return;
    const arrivalTime = status === 'late' ? new Date().toTimeString().slice(0, 5) : null;
    const nextParticipants = detail.participants.map((p) =>
      (p.sophomorixUnid || p.Student_ID || p.username) === studentKey
        ? { ...p, attendance_status: status, arrival_time: arrivalTime }
        : p
    );
    const nextAttendanceCount = nextParticipants.filter((p) => p.attendance_status).length;
    const nextAbsentCount = nextParticipants.filter((p) => p.attendance_status === 'absent').length;
    const nextLateCount = nextParticipants.filter((p) => p.attendance_status === 'late').length;

    setDetail((prev) => (prev ? { ...prev, participants: nextParticipants } : prev));
    setEntries((prev) =>
      prev.map((e) =>
        e.id === selectedEntryId
          ? applyEntryGridPatch(
              { ...e, absent_count: nextAbsentCount, late_count: nextLateCount },
              { attendance_count: nextAttendanceCount },
              detail.unit
            )
          : e
      )
    );

    try {
      await klassenbuchApi.setAttendance(selectedEntryId, studentKey, { status, arrival_time: arrivalTime });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Status nicht speicherbar');
      const refreshed = await klassenbuchApi.getEntry(selectedEntryId);
      setDetail(refreshed);
      const from = toDateStr(weekStart);
      const to = toDateStr(addDays(weekStart, 6));
      const fresh = await klassenbuchApi.listEntries({ unitType, unitId: unitId!, schoolYearId, from, to });
      setEntries(fresh);
    }
  }

  async function handleCancelToggle() {
    if (!detail || readOnlyDetail || !selectedEntryId) return;
    const next = !isTruthyFlag(detail.unit.is_cancelled);
    await saveDetailFields({ is_cancelled: next ? 1 : 0 });
    toast.success(next ? 'Stunde als ausgefallen markiert' : 'Ausfallmarkierung entfernt');
  }

  async function handleLockToggle() {
    if (!detail) return;
    try {
      if (detail.unit.klassenbuch_locked_at) {
        await klassenbuchApi.unlock(detail.unit.id);
        toast.success('Eintrag wieder geöffnet');
      } else {
        await klassenbuchApi.lock(detail.unit.id);
        toast.success('Eintrag abgeschlossen');
      }
      const refreshed = await klassenbuchApi.getEntry(detail.unit.id);
      setDetail(refreshed);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Aktion nicht möglich');
    }
  }

  async function handleNotifyHomework() {
    if (!selectedEntryId) return;
    try {
      const res = await klassenbuchApi.notifyHomework(selectedEntryId);
      toast.success(`Hausaufgabe versendet: ${res.sent}/${res.recipientCount}`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Versand fehlgeschlagen');
    }
  }

  async function handleAnnouncement() {
    if (!unitType || !unitId) return;
    const subject = window.prompt('Betreff der Mitteilung:');
    if (!subject) return;
    const text = window.prompt('Text der Mitteilung:');
    if (!text) return;
    try {
      const result = await klassenbuchApi.createAnnouncement({
        unit_type: unitType,
        unit_id: unitId,
        subject,
        text
      });
      toast.success(`Mitteilung versendet: ${result.sent}/${result.recipientCount}`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Versand fehlgeschlagen');
    }
  }

  async function handleSaveCurriculumLinks(rawInput: string) {
    if (!selectedEntryId) return;
    const ids = rawInput
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((x) => Number.isFinite(x) && x > 0);
    try {
      const links = ids.map((id) => ({ curriculum_id: id }));
      await klassenbuchApi.saveCurriculumLinks(selectedEntryId, links);
      const fresh = await klassenbuchApi.getCurriculumLinks(selectedEntryId);
      setCurriculumLinks(fresh);
      toast.success('Curricula gespeichert');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Curricula konnten nicht gespeichert werden');
    }
  }

  async function handleApplySuggestedCompetencies(selectedIds: number[]) {
    if (!selectedEntryId || !selectedIds.length) return;
    try {
      const result = await klassenbuchApi.applyCompetencies(selectedEntryId, selectedIds);
      toast.success(`Kompetenzen übernommen (${result.inserted})`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Kompetenzen konnten nicht übernommen werden');
    }
  }

  async function handleCreateSimpleGrade(studentId: string) {
    if (!selectedEntryId) return;
    const grade = window.prompt('Note (z.B. 2+)');
    if (!grade) return;
    try {
      await klassenbuchApi.createUnitGrade(selectedEntryId, {
        student_id: studentId,
        title: 'Mündliche Leistung',
        grade_value: grade,
        graded_at: new Date().toISOString().slice(0, 10),
        status: 'draft'
      });
      const rows = await klassenbuchApi.listUnitGrades(selectedEntryId);
      setUnitGrades(rows);
      toast.success('Note erfasst');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Notenerfassung fehlgeschlagen');
    }
  }

  async function handleCreateStaffNote() {
    if (!selectedEntryId) return;
    const noteText = window.prompt('Notizzettel für Kollegium:');
    if (!noteText) return;
    try {
      await klassenbuchApi.createStaffNote({
        scope_type: 'teaching_unit',
        scope_id: String(selectedEntryId),
        note_text: noteText,
        notify_email: true
      });
      const rows = await klassenbuchApi.listStaffNotes({
        scope_type: 'teaching_unit',
        scope_id: String(selectedEntryId)
      });
      setStaffNotes(rows);
      toast.success('Notizzettel gespeichert');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Notizzettel konnte nicht gespeichert werden');
    }
  }

  async function handleCreateEvent() {
    const title = window.prompt('Titel des Kalendereintrags:');
    if (!title) return;
    const day = toDateStr(weekStart);
    try {
      await klassenbuchApi.createEvent({
        title,
        starts_at: `${day} 08:00:00`,
        ends_at: `${day} 09:00:00`,
        target_scope: unitType === 'class' ? 'class' : unitType === 'course' ? 'course' : 'group',
        target_id: unitId || null
      });
      const rows = await klassenbuchApi.listEvents(toDateStr(weekStart), toDateStr(addDays(weekStart, 6)));
      setEvents(rows);
      toast.success('Termin angelegt');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Termin konnte nicht gespeichert werden');
    }
  }

  const readOnlyDetail = !!detail?.unit.klassenbuch_locked_at;

  return (
    <div className="p-4 md:p-6 space-y-4 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Klassenbuch</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleAnnouncement}
            disabled={!unitId}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Mitteilung an Klasse/Kurs senden"
          >
            <Megaphone size={16} /> Mitteilung
          </button>
          <button
            type="button"
            onClick={() => selectedEntryId && setQuickMode(true)}
            disabled={!selectedEntryId}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
            title="Quick Mode – minimal-ablenkung im Unterricht"
          >
            <Rocket size={16} /> Quick Mode
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block text-sm md:col-span-2">
          <span className="text-gray-700 dark:text-gray-300">Klasse / Kurs / Gruppe</span>
          <select
            value={unitId ? `${unitType}:${unitId}` : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setUnitId(null);
                return;
              }
              const [t, idStr] = v.split(':');
              setUnitType(t as UnitType);
              setUnitId(Number(idStr));
              setSelectedEntryId(null);
            }}
            className="mt-1 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">– wählen –</option>
            {unitOptions.map((o) => (
              <option key={`${o.unitType}:${o.unitId}`} value={`${o.unitType}:${o.unitId}`}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Schuljahr</span>
          <select
            value={schoolYearId ?? ''}
            onChange={(e) => setSchoolYearId(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">Alle</option>
            {schoolYears.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-1">
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
          >
            <CalendarDays size={14} /> Heute
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Wochenraster */}
      {periods.length > 0 ? (
        <div className="overflow-x-auto">
          <div
            className="grid gap-1 min-w-[720px]"
            style={{
              gridTemplateColumns: `90px repeat(${activeDays.length}, minmax(140px, 1fr))`
            }}
          >
            {/* Kopfzeile */}
            <div />
            {weekDays.map((d, idx) => {
              const key = toDateStr(d);
              const isToday = key === toDateStr(new Date());
              return (
                <div
                  key={`head-${key}`}
                  className={`text-xs font-semibold px-2 py-1.5 rounded-md ${
                    isToday
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200'
                      : 'bg-gray-50 text-gray-700 dark:bg-gray-900 dark:text-gray-200'
                  }`}
                >
                  {DAY_SHORT[activeDays[idx]]} ·{' '}
                  {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </div>
              );
            })}

            {/* Periodenzeilen */}
            {periods.map((p, idx) => (
              <FragmentRow
                key={p.period_no}
                period={p}
                displayLabel={normalizeDisplayPeriodLabel(p, idx)}
                weekDays={weekDays}
                map={entriesByCell.map}
                scheduleSlots={scheduleSlotsByCell}
                selectedEntryId={selectedEntryId}
                onSelect={(id) => setSelectedEntryId(id)}
                onCreate={createEntryForCell}
                canCreate={!!unitType && !!unitId}
                creatingKey={creatingCellKey}
              />
            ))}
          </div>

          {/* Nicht zugeordnete Einträge (ausserhalb des Rasters) */}
          {Object.keys(entriesByCell.unassignedByDay).length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                Weitere Einträge ausserhalb des Rasters
              </div>
              <div
                className="grid gap-1"
                style={{
                  gridTemplateColumns: `90px repeat(${activeDays.length}, minmax(140px, 1fr))`
                }}
              >
                <div />
                {weekDays.map((d) => {
                  const key = toDateStr(d);
                  const list = entriesByCell.unassignedByDay[key] || [];
                  return (
                    <div key={`extra-${key}`} className="space-y-1">
                      {list.map((e) => (
                        <EntryButton
                          key={e.id}
                          entry={e}
                          selected={selectedEntryId === e.id}
                          onClick={() => setSelectedEntryId(e.id)}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {unitType && unitId && (
            <div className="text-xs text-amber-700 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 mb-2">
              Kein Stundenraster konfiguriert. Unter{' '}
              <a href="/settings?tab=klassenbuch" className="underline">
                Einstellungen → Klassenbuch
              </a>{' '}
              können Sie Rahmenzeiten und Perioden festlegen.
            </div>
          )}
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: `repeat(${activeDays.length}, minmax(0, 1fr))` }}
          >
            {weekDays.map((d) => {
              const key = toDateStr(d);
              const dayEntries = entriesByDay[key] || [];
              const isToday = key === toDateStr(new Date());
              return (
                <div
                  key={key}
                  className={`border rounded-lg p-2 min-h-[160px] ${
                    isToday
                      ? 'border-blue-400 bg-blue-50/40 dark:border-blue-700 dark:bg-blue-950/40'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                  }`}
                >
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                    {d.toLocaleDateString('de-DE', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit'
                    })}
                  </div>
                  <div className="space-y-1">
                    {dayEntries.length === 0 && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">Keine Einträge</div>
                    )}
                    {dayEntries.map((e) => (
                      <EntryButton
                        key={e.id}
                        entry={e}
                        selected={selectedEntryId === e.id}
                        onClick={() => setSelectedEntryId(e.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Detail-Panel */}
      {selectedEntryId && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
          {loadingDetail || !detail ? (
            <div className="p-6 text-gray-500 dark:text-gray-400">Lade Details…</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {detail.unit.class_name || detail.unit.course_name || detail.unit.group_name || ''}
                    {detail.unit.start_at ? ` · ${new Date(detail.unit.start_at).toLocaleString('de-DE')}` : ''}
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{detail.unit.title}</div>
                  {(detail.linkedClassEntries?.length || 0) > 1 ? (
                    <div className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
                      Synchron mit:{' '}
                      {detail.linkedClassEntries!
                        .map((entry) => entry.class_name || entry.course_name || `#${entry.parent_id}`)
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  ) : null}
                  {detail.participantSource === 'course_selection' && (detail.courseClassLinks?.length || 0) > 0 ? (
                    <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                      Kurs-Teilnehmer (Auswahl aus {detail.courseClassLinks!.map((link) => link.class_name).join(', ')}):
                      {' '}
                      {detail.participants?.length ?? 0} Schüler
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <AcknowledgmentBadge acknowledgments={detail.acknowledgments} />
                  <button
                    type="button"
                    onClick={() => void handleCancelToggle()}
                    disabled={readOnlyDetail}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm disabled:opacity-50 ${
                      isTruthyFlag(detail.unit.is_cancelled)
                        ? 'border-gray-300 bg-gray-50 text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                        : 'border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/40 dark:text-red-100'
                    }`}
                    title="Stunde als ausgefallen markieren"
                  >
                    <Ban size={14} />
                    {isTruthyFlag(detail.unit.is_cancelled) ? 'Ausfall aufheben' : 'Ausgefallen'}
                  </button>
                  <button
                    type="button"
                    onClick={handleLockToggle}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm ${
                      readOnlyDetail
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                    }`}
                  >
                    {readOnlyDetail ? (
                      <>
                        <Unlock size={14} /> Wieder öffnen
                      </>
                    ) : (
                      <>
                        <Lock size={14} /> Abschließen
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 px-4 flex items-center gap-1 overflow-x-auto">
                {TABS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActiveTab(t)}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                      activeTab === t
                        ? 'border-blue-600 text-blue-700 font-semibold'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {
                      {
                        content: 'Thema/Inhalt',
                        homework: 'Hausaufgaben',
                        attendance: 'Anwesenheit',
                        excuses: 'Entschuldigungen',
                        curriculum: 'Curricula/Kompetenzen',
                        performance: 'Leistung/Noten',
                        notes: 'Notizzettel',
                        calendar: 'Kalender',
                        audit: 'Audit'
                      }[t]
                    }
                  </button>
                ))}
              </div>

              <div className="p-4 space-y-4">
                {activeTab === 'content' && (
                  <>
                    <label className="block text-sm">
                      <span className="text-gray-700 dark:text-gray-300">Thema der Stunde</span>
                      <input
                        type="text"
                        defaultValue={detail.unit.topic_summary || ''}
                        disabled={readOnlyDetail}
                        onBlur={(e) => void saveDetailFields({ topic_summary: e.currentTarget.value || null })}
                        className="mt-1 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
                        placeholder="Kurzthema, erscheint in der Wochenübersicht"
                      />
                    </label>
                    <LessonContentEditor
                      label="Inhalt/Verlauf"
                      value={detail.unit.lesson_content_html}
                      readOnly={readOnlyDetail}
                      onSave={(html) => saveDetailFields({ lesson_content_html: html })}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          defaultChecked={!!detail.unit.is_substitution}
                          disabled={readOnlyDetail}
                          onChange={(e) => void saveDetailFields({ is_substitution: e.currentTarget.checked ? 1 : 0 })}
                        />
                        Vertretungsstunde
                      </label>
                      <label className="block text-sm">
                        <span className="text-gray-700 dark:text-gray-300">Vertretung für</span>
                        <select
                          value={detail.unit.substitution_for_user_id || ''}
                          disabled={readOnlyDetail}
                          onChange={(e) =>
                            void saveDetailFields({
                              substitution_for_user_id: e.target.value ? Number(e.target.value) : null
                            })
                          }
                          className="mt-1 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">– auswählen –</option>
                          {teachers.map((t) => (
                            <option key={t.id} value={t.id}>
                              {(t.last_name || t.username || '').trim()}, {(t.first_name || '').trim()}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-sm">
                        <span className="text-gray-700 dark:text-gray-300">Vertretungsgrund</span>
                        <input
                          type="text"
                          defaultValue={detail.unit.substitution_reason || ''}
                          disabled={readOnlyDetail}
                          onBlur={(e) => void saveDetailFields({ substitution_reason: e.currentTarget.value || null } as any)}
                          className="mt-1 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
                        />
                      </label>
                    </div>
                  </>
                )}

                {activeTab === 'homework' && (
                  <>
                    <LessonContentEditor
                      label="Hausaufgaben"
                      value={detail.unit.homework_html}
                      readOnly={readOnlyDetail}
                      onSave={(html) => saveDetailFields({ homework_html: html })}
                    />
                    <label className="block text-sm">
                      <span className="text-gray-700 dark:text-gray-300">Fällig am</span>
                      <input
                        type="date"
                        defaultValue={detail.unit.homework_due_date || ''}
                        disabled={readOnlyDetail}
                        onBlur={(e) => void saveDetailFields({ homework_due_date: e.currentTarget.value || null })}
                        className="mt-1 w-60 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm dark:[color-scheme:dark]"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleNotifyHomework}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                    >
                      <Send size={14} /> Hausaufgaben per E-Mail an Eltern senden
                    </button>
                  </>
                )}

                {activeTab === 'attendance' && (
                  <AttendanceSeatplan
                    participants={detail.participants}
                    disabled={readOnlyDetail}
                    onSet={(key, status) => void handleSetAttendance(key, status)}
                    onOpenExcuse={(p) => setExcuseParticipant(p)}
                    parentType={detail.unit.parent_type}
                    parentId={detail.unit.parent_id}
                    className={detail.unit.class_name}
                    groupName={detail.unit.group_name}
                  />
                )}

                {activeTab === 'excuses' && (
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    {detail.participants
                      .flatMap((p) =>
                        (p.active_excuses || []).map((ex) => ({
                          participant: p,
                          excuse: ex
                        }))
                      )
                      .map(({ participant, excuse }) => (
                        <div
                          key={excuse.id}
                          className="border border-gray-200 dark:border-gray-700 rounded-md p-2 flex items-center justify-between gap-3 bg-white dark:bg-gray-900"
                        >
                          <div>
                            <div className="font-medium">
                              {participant.last_name || participant.Name || ''}, {participant.first_name || participant.Vorname || participant.username}
                            </div>
                            <div className="text-xs text-gray-500">
                              {excuse.kind} · {excuse.valid_from} – {excuse.valid_to} · {excuse.approval_status}
                            </div>
                            {excuse.reason && <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">{excuse.reason}</div>}
                          </div>
                          <div className="flex items-center gap-1">
                            {excuse.approval_status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const result = await klassenbuchApi.approveExcuse(excuse.id, true);
                                      const applied = Number(result?.attendance_apply?.applied || 0);
                                      const dailyApplied = Number(result?.attendance_apply?.dailyApplied || 0);
                                      const dayCount = Math.max(applied, dailyApplied);
                                      toast.success(
                                        dayCount > 0
                                          ? `Entschuldigung genehmigt (${dayCount} Tag${dayCount === 1 ? '' : 'e'} berücksichtigt${applied > 0 ? `, ${applied} Stunde${applied === 1 ? '' : 'n'}` : ''})`
                                          : 'Entschuldigung genehmigt'
                                      );
                                      if (selectedEntryId) {
                                        const d = await klassenbuchApi.getEntry(selectedEntryId);
                                        setDetail(d);
                                      }
                                    } catch (err) {
                                      const e = err as { response?: { data?: { error?: string } } };
                                      toast.error(e.response?.data?.error || 'Fehler');
                                    }
                                  }}
                                  className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                                >
                                  Genehmigen
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await klassenbuchApi.approveExcuse(excuse.id, false);
                                      toast.success('Entschuldigung abgelehnt');
                                    } catch {
                                      toast.error('Fehler');
                                    }
                                  }}
                                  className="text-xs px-2 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700"
                                >
                                  Ablehnen
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    {detail.participants.every((p) => !(p.active_excuses || []).length) && (
                      <div className="text-sm text-gray-500">Keine aktiven Entschuldigungen/Beurlaubungen im Zeitraum.</div>
                    )}
                  </div>
                )}

                {activeTab === 'curriculum' && (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Curricula verknüpfen (Komma-separierte Curriculum-IDs):
                    </div>
                    <input
                      type="text"
                      defaultValue={curriculumLinks.map((x) => x.curriculum_id).join(',')}
                      disabled={readOnlyDetail}
                      onBlur={(e) => void handleSaveCurriculumLinks(e.currentTarget.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
                      placeholder="z.B. 3,7,11"
                    />
                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Kompetenz-Vorschläge
                      </div>
                      {competencySuggestions.length === 0 ? (
                        <div className="text-xs text-gray-500">Keine Vorschläge vorhanden</div>
                      ) : (
                        <div className="space-y-1">
                          {competencySuggestions.map((s) => (
                            <label key={s.competency_id} className="flex items-center gap-2 text-sm">
                              <input type="checkbox" defaultChecked />
                              <span>{s.competency_name}</span>
                            </label>
                          ))}
                          <button
                            type="button"
                            disabled={readOnlyDetail}
                            onClick={() =>
                              void handleApplySuggestedCompetencies(
                                competencySuggestions.map((x) => x.competency_id)
                              )
                            }
                            className="mt-2 inline-flex items-center gap-1 rounded-md border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 px-2 py-1 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                          >
                            Kompetenzen übernehmen
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'performance' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <GraduationCap size={14} /> Noten je Unterrichtseinheit
                    </div>
                    {detail.participants.map((p) => {
                      const key = p.sophomorixUnid || p.Student_ID || p.username;
                      const personGrades = unitGrades.filter((g) => g.student_id === key);
                      return (
                        <div key={key} className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-medium">
                              {p.last_name || p.Name || ''}, {p.first_name || p.Vorname || p.username}
                            </div>
                            <button
                              type="button"
                              onClick={() => key && void handleCreateSimpleGrade(key)}
                              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              Note erfassen
                            </button>
                          </div>
                          <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                            {personGrades.length
                              ? personGrades
                                  .slice(0, 4)
                                  .map((g) => `${g.grade_value} (${String(g.graded_at || '').slice(0, 10)})`)
                                  .join(', ')
                              : 'Keine Noten'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {activeTab === 'notes' && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => void handleCreateStaffNote()}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <StickyNote size={14} /> Notizzettel anlegen
                    </button>
                    {staffNotes.length ? (
                      <div className="space-y-2">
                        {staffNotes.map((note) => (
                          <div key={note.id} className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
                            <div className="text-sm text-gray-900 dark:text-gray-100">{note.note_text}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(note.created_at).toLocaleString('de-DE')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Noch keine Notizzettel.</div>
                    )}
                  </div>
                )}

                {activeTab === 'calendar' && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => void handleCreateEvent()}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <CalendarDays size={14} /> Termin anlegen
                    </button>
                    {events.length ? (
                      <div className="space-y-2">
                        {events.map((ev) => (
                          <div key={ev.id} className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{ev.title}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(ev.starts_at).toLocaleString('de-DE')} - {new Date(ev.ends_at).toLocaleString('de-DE')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Keine Termine in dieser Woche.</div>
                    )}
                  </div>
                )}

                {activeTab === 'audit' && (
                  <AuditPanel entityType="teaching_unit" entityId={detail.unit.id} />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {excuseParticipant && (
        <ExcuseModal
          participant={excuseParticipant}
          defaultFrom={detail?.unit.start_at?.slice(0, 10)}
          defaultTo={detail?.unit.start_at?.slice(0, 10)}
          onClose={() => setExcuseParticipant(null)}
          onCreated={async () => {
            if (selectedEntryId) {
              const d = await klassenbuchApi.getEntry(selectedEntryId);
              setDetail(d);
            }
          }}
        />
      )}

      {quickMode && selectedEntryId && (
        <QuickLessonMode
          teachingUnitId={selectedEntryId}
          onClose={() => setQuickMode(false)}
          onLocked={async () => {
            if (selectedEntryId) {
              const d = await klassenbuchApi.getEntry(selectedEntryId);
              setDetail(d);
            }
          }}
        />
      )}
    </div>
  );
}
